import * as React from "react"
import { Drawer as DrawerPrimitive } from "vaul"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { withBackClose } from "./history-aware-root"

// The ONE bottom sheet of the CRM on a phone (DESIGN_SYSTEM.md §23), ported from Jamora's `components/ui/mobile-sheet.tsx` so both
// products feel the same: `vaul` brings the native drag-to-close (follows the finger, inertia, rubber band, a drag started inside a
// scrolled list scrolls the list), Header/Body/Footer keep the title and the buttons in view while only the body scrolls, the
// close button is a 44 px target named « Fermer », and the bottom safe area is handled here once. Android's Back closes it
// (`withBackClose`, same as every overlay of the CRM). A sheet opened from inside another one is a vaul NestedRoot: the parent stays
// visible underneath.
//
// vaul is built on Radix Dialog; `package.json` resolutions pin it to the CRM's own copy, so a Radix overlay opened from inside a
// MobileSheet (the task form opened by the Copilote) is one more layer of the same stack, not « a click outside » that would close it.

const InsideMobileSheet = React.createContext(false)
const Root = withBackClose(DrawerPrimitive.Root)
const NestedRoot = withBackClose(DrawerPrimitive.NestedRoot)

function MobileSheet({ children, ...props }) {
  const nested = React.useContext(InsideMobileSheet)
  const Primitive = nested ? NestedRoot : Root
  return <Primitive {...props}>{children}</Primitive>
}

const MobileSheetTrigger = DrawerPrimitive.Trigger
const MobileSheetClose = DrawerPrimitive.Close
const MobileSheetPortal = DrawerPrimitive.Portal

const MobileSheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay ref={ref} className={cn("fixed inset-0 z-50 bg-black/40", className)} {...props} />
))
MobileSheetOverlay.displayName = "MobileSheetOverlay"

/** Ready-made close button (44 px target), to place in MobileSheetHeader next to the title. */
const MobileSheetCloseButton = React.forwardRef(({ className, ...props }, ref) => (
  <DrawerPrimitive.Close
    ref={ref}
    className={cn(
      "-mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-slate-500 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      className
    )}
    {...props}>
    <X className="h-5 w-5" aria-hidden="true" />
    <span className="sr-only">Fermer</span>
  </DrawerPrimitive.Close>
))
MobileSheetCloseButton.displayName = "MobileSheetCloseButton"

// 90 % of the dynamic viewport minus the top safe area at most: a slice of the page stays visible, so it reads as a panel over the
// page. A caller that truly needs the whole screen (the Copilote's conversation) overrides the height through `className`.
// `closeOnHandle`: a sheet without a header gets its « Fermer » on the drag handle's line (right side), never floating lower down;
// a sheet with a header puts MobileSheetCloseButton in MobileSheetHeader, on the title's line.
const MobileSheetContent = React.forwardRef(({ className, closeOnHandle = false, children, ...props }, ref) => (
  <MobileSheetPortal>
    <MobileSheetOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        "fixed inset-x-0 bottom-0 z-50 flex max-h-[calc(90dvh-env(safe-area-inset-top,0px))] flex-col rounded-t-[20px] border-t bg-background pb-[env(safe-area-inset-bottom,0px)] shadow-xl outline-none",
        className
      )}
      {...props}>
      {closeOnHandle ? (
        <div className="relative flex h-11 shrink-0 items-center justify-center">
          <DrawerPrimitive.Handle className="h-1.5 w-10 rounded-full bg-slate-300" />
          <MobileSheetCloseButton className="absolute right-2 top-0 mr-0" />
        </div>
      ) : (
        <DrawerPrimitive.Handle className="mx-auto mt-3 h-1.5 w-10 shrink-0 rounded-full bg-slate-300" />
      )}
      <InsideMobileSheet.Provider value>{children}</InsideMobileSheet.Provider>
    </DrawerPrimitive.Content>
  </MobileSheetPortal>
))
MobileSheetContent.displayName = "MobileSheetContent"


const MobileSheetHeader = ({ className, ...props }) => (
  <div className={cn("flex shrink-0 items-center justify-between gap-3 px-4 pb-3 pt-2", className)} {...props} />
)
MobileSheetHeader.displayName = "MobileSheetHeader"

const MobileSheetTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title ref={ref} className={cn("text-base font-semibold text-slate-900", className)} {...props} />
))
MobileSheetTitle.displayName = "MobileSheetTitle"

const MobileSheetDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
))
MobileSheetDescription.displayName = "MobileSheetDescription"

/** The only scrolling area: header and footer stay in view around it. */
const MobileSheetBody = ({ className, ...props }) => (
  <div className={cn("min-h-0 flex-1 overflow-y-auto px-4 pb-4", className)} {...props} />
)
MobileSheetBody.displayName = "MobileSheetBody"

const MobileSheetFooter = ({ className, ...props }) => (
  <div className={cn("flex shrink-0 flex-col gap-2 border-t bg-background px-4 pb-4 pt-3", className)} {...props} />
)
MobileSheetFooter.displayName = "MobileSheetFooter"

export {
  MobileSheet,
  MobileSheetTrigger,
  MobileSheetClose,
  MobileSheetPortal,
  MobileSheetOverlay,
  MobileSheetContent,
  MobileSheetCloseButton,
  MobileSheetHeader,
  MobileSheetTitle,
  MobileSheetDescription,
  MobileSheetBody,
  MobileSheetFooter,
}
